import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const FilterChips = ({ filters, onRemoveFilter, onClearAll }) => {
  const chips = [];

  // Province
  if (filters.province) {
    const provinceName = filters.province.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    chips.push({
      key: 'province',
      label: provinceName,
      icon: 'fa-map',
      onRemove: () => onRemoveFilter('province')
    });
  }

  // City
  if (filters.city) {
    chips.push({
      key: 'city',
      label: filters.city,
      icon: 'fa-map-marker-alt',
      onRemove: () => onRemoveFilter('city')
    });
  }

  // Listing Type
  if (filters.listingType) {
    chips.push({
      key: 'listingType',
      label: filters.listingType,
      icon: filters.listingType === 'For Sale' ? 'fa-tag' : 'fa-key',
      onRemove: () => onRemoveFilter('listingType')
    });
  }

  // Property Types
  if (filters.propertyTypes?.length > 0) {
    filters.propertyTypes.forEach((type, index) => {
      chips.push({
        key: `propertyType-${index}`,
        label: type,
        icon: 'fa-home',
        onRemove: () => onRemoveFilter('propertyType', type)
      });
    });
  }

  // Price Range
  if (filters.minPrice || filters.maxPrice) {
    const formatPrice = (val) => val >= 1000000 ? `R${(val/1000000).toFixed(1)}M` : `R${(val/1000).toFixed(0)}K`;
    const min = filters.minPrice || 0;
    const max = filters.maxPrice || 50000000;
    if (min > 0 || max < 50000000) {
      chips.push({
        key: 'price',
        label: `${formatPrice(min)} - ${formatPrice(max)}`,
        icon: 'fa-rand-sign',
        onRemove: () => onRemoveFilter('price')
      });
    }
  }

  // Bedrooms
  if (filters.minBeds) {
    chips.push({
      key: 'beds',
      label: `${filters.minBeds}+ Beds`,
      icon: 'fa-bed',
      onRemove: () => onRemoveFilter('minBeds')
    });
  }

  // Bathrooms
  if (filters.minBaths) {
    chips.push({
      key: 'baths',
      label: `${filters.minBaths}+ Baths`,
      icon: 'fa-bath',
      onRemove: () => onRemoveFilter('minBaths')
    });
  }

  // Size
  if (filters.minSize || filters.maxSize) {
    const min = filters.minSize || 0;
    const max = filters.maxSize ? `${filters.maxSize}` : '∞';
    chips.push({
      key: 'size',
      label: `${min}-${max} sqm`,
      icon: 'fa-ruler-combined',
      onRemove: () => onRemoveFilter('size')
    });
  }

  // Amenities
  const amenityLabels = {
    'pool': 'Pool',
    'garden': 'Garden',
    'garage': 'Garage',
    'fiber': 'Fiber',
    'pet-friendly': 'Pet Friendly'
  };
  if (filters.amenities?.length > 0) {
    filters.amenities.forEach((amenity, index) => {
      chips.push({
        key: `amenity-${index}`,
        label: amenityLabels[amenity] || amenity,
        icon: 'fa-check-circle',
        onRemove: () => onRemoveFilter('amenity', amenity)
      });
    });
  }

  if (chips.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2 py-3" data-testid="filter-chips">
      <span className="text-sm text-gray-500 font-medium">Active filters:</span>
      {chips.map((chip) => (
        <Badge
          key={chip.key}
          variant="secondary"
          className="flex items-center gap-1 px-3 py-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
        >
          <i className={`fas ${chip.icon} text-xs`}></i>
          <span>{chip.label}</span>
          <button
            onClick={chip.onRemove}
            className="ml-1 hover:text-emerald-900"
            data-testid={`remove-filter-${chip.key}`}
          >
            <i className="fas fa-times text-xs"></i>
          </button>
        </Badge>
      ))}
      {chips.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearAll}
          className="text-gray-500 hover:text-gray-700 h-7"
          data-testid="clear-all-filters"
        >
          <i className="fas fa-times-circle mr-1"></i> Clear all
        </Button>
      )}
    </div>
  );
};

export default FilterChips;
